/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, PlusCircle, BookOpen, Building2, FileText, AlertTriangle
} from 'lucide-react';
import {
  knowledgeBaseService, AI_AGENT_ERROR_CODES, getApiErrorMessage
} from '../services/knowledgeBaseService';
import { accountService } from '../services/accountService';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import { ACCOUNT_LABEL } from '../constants/entityLabels';

const LIST_ROUTE = '/knowledge-bases';
const NAME_MAX_LENGTH = 200;
const DESCRIPTION_MAX_LENGTH = 500;

const NewKnowledgeBase = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // Platform-wide view picks the account; account / sub-account users are pinned to their own.
  const showAccountField = !user?.accountUID;

  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [integrationError, setIntegrationError] = useState(null);
  const [form, setForm] = useState({
    accountId: searchParams.get('accountId') || '',
    name: '',
    description: '',
  });

  useEffect(() => {
    if (!showAccountField) { return; }
    const fetchAccounts = async () => {
      try {
        const { data } = await accountService.getAccounts({ limit: 100 });
        setAccounts(data.data || data || []);
      } catch {
        toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
      }
    };
    fetchAccounts();
  }, [showAccountField]);

  const backToList = () => {
    navigate(showAccountField && form.accountId
      ? `${LIST_ROUTE}?accountId=${encodeURIComponent(form.accountId)}`
      : LIST_ROUTE);
  };

  const validateForm = () => {
    const errors = {};
    if (showAccountField && !form.accountId) { errors.accountId = `${ACCOUNT_LABEL.singular} is required`; }
    if (!form.name.trim()) { errors.name = 'Name is required'; }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (field === 'name' && !value.trim()) {
      setFieldErrors(prev => ({ ...prev, name: 'Name is required' }));
    } else if (field === 'accountId' && showAccountField && !value) {
      setFieldErrors(prev => ({ ...prev, accountId: `${ACCOUNT_LABEL.singular} is required` }));
    } else {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }
    try {
      setSubmitting(true);
      setIntegrationError(null);
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        ...(showAccountField && { accountId: form.accountId }),
      };
      await knowledgeBaseService.createKnowledgeBase(payload);
      toastService.success('Knowledge base created successfully');
      backToList();
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to create knowledge base');
      if (AI_AGENT_ERROR_CODES.includes(err.response?.data?.code)) {
        setIntegrationError(message);
      }
      toastService.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={backToList}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Knowledge Bases
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="space-y-1">
          <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
            New Record
          </span>
          <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
            Create Knowledge Base
          </h1>
          <p className="text-xs text-base-content/50">
            Add a knowledge base to the {ACCOUNT_LABEL.singularLower}'s AI Agent.
          </p>
        </div>
      </div>

      {integrationError && (
        <div role="alert" className="alert alert-warning rounded-xl text-sm font-semibold">
          <AlertTriangle size={18} className="shrink-0" />
          <span>{integrationError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Account (platform-wide view only) */}
        {showAccountField && (
          <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
              <Building2 size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                {ACCOUNT_LABEL.singular}
              </h2>
            </div>

            <div className="space-y-1.5 max-w-xl">
              <label htmlFor="accountId" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                {ACCOUNT_LABEL.singular} *
              </label>
              <select
                id="accountId"
                className={inputClass('accountId')}
                value={form.accountId}
                onBlur={e => handleBlur('accountId', e.target.value)}
                onChange={e => {
                  setForm(f => ({ ...f, accountId: e.target.value }));
                  setFieldErrors(p => ({ ...p, accountId: null }));
                  setIntegrationError(null);
                }}
              >
                <option value="">— Select {ACCOUNT_LABEL.withArticle} —</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
              {fieldErrors.accountId && <p className="text-error text-xs font-medium">{fieldErrors.accountId}</p>}
              <p className="text-base-content/40 text-[10px] font-medium">
                The {ACCOUNT_LABEL.singularLower} must have AI Agent integration enabled.
              </p>
            </div>
          </div>
        )}

        {/* Knowledge Base Information */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <BookOpen size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              Knowledge Base Information
            </h2>
          </div>

          <div className="space-y-1.5 max-w-xl">
            <label htmlFor="name" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
              <BookOpen size={12} className="text-primary" />
              <span>Name *</span>
            </label>
            <input
              id="name"
              type="text"
              maxLength={NAME_MAX_LENGTH}
              placeholder="e.g. Hotel Inspection"
              className={inputClass('name')}
              value={form.name}
              onBlur={e => handleBlur('name', e.target.value)}
              onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setFieldErrors(p => ({ ...p, name: null })); }}
            />
            {fieldErrors.name && <p className="text-error text-xs font-medium">{fieldErrors.name}</p>}
          </div>

          <div className="space-y-1.5 max-w-xl">
            <label htmlFor="description" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
              <FileText size={12} className="text-primary" />
              <span>Description</span>
            </label>
            <textarea
              id="description"
              rows={4}
              maxLength={DESCRIPTION_MAX_LENGTH}
              placeholder="e.g. Details about different hotel equipment"
              className={`${inputClass('description')} resize-y`}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            />
            <p className="text-base-content/40 text-[10px] font-medium text-right">
              {form.description.length}/{DESCRIPTION_MAX_LENGTH}
            </p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={backToList}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Creating Knowledge Base...</span>
              </>
            ) : (
              <>
                <PlusCircle size={16} />
                <span>Create Knowledge Base</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewKnowledgeBase;
